import { defineStore } from 'pinia'
import authClient from '@/api/auth/client'
import { ref } from 'vue'
import privateClient from '@/api/private/client'

export const useSocialStore = defineStore('social', () => {
    const availableSocials = import.meta.env.VITE_AVAILABLE_SOCIALS.split(',').filter(Boolean)
    const connectedSocials = ref<{ provider: string, providerUsername: string }[]>([])
    const lastSocialError = ref<string | null>(null)

    const loadConnectedSocials = async () => {
        const result = await privateClient.socials()

        const resp = result.data?.data
        if (!resp) {
            return
        }

        const connections = resp
            .map((social: {
                provider: string,
                provider_username?: string
            }) => ({
                provider: social.provider,
                providerUsername: social.provider_username
                    ?? `${social.provider.charAt(0).toUpperCase() + social.provider.slice(1)} user`,
            }))

        connectedSocials.value.splice(0, connectedSocials.value.length)

        connections.forEach((b) => connectedSocials.value.push(b))
    }

    const initSocialLogin = async (provider: string)=> {
        const { data, error } = await authClient
            .initSocialLogin({
                provider,
            })

        handleAPIError(error)

        if (error || !data) {
            return ''
        }

        return data.data.auth_url
    }

    const confirmSocialLogin = async (provider: string, code: string, state: string) => {
        const { error } = await authClient
            .confirmSocialLogin({
                provider,
                code,
                state,
            })

        handleAPIError(error)

        return !error
    }

    const connectSocial = async (provider: string) => {
        const { data, error } = await privateClient
            .connectSocial(provider)

        handleAPIError(error)

        if (error || !data) {
            return ''
        }

        return data.data.auth_url
    }

    const confirmSocial = async (provider: string, code: string, state: string)=> {
        const { error } = await privateClient
            .confirmSocial(provider, {
                code,
                state,
            })

        handleAPIError(error)

        if (error) {
            return
        }

        await loadConnectedSocials()
    }

    const disconnectSocial = async (provider: string) => {
        const { error } = await privateClient.disconnectSocial(provider)

        handleAPIError(error)
        if (error) {
            return
        }

        await loadConnectedSocials()
    }

    const handleAPIError = (error: { code: number, messages: string[] } | undefined) => {
        if (error?.messages.length) {
            lastSocialError.value = error.messages[0] ?? null

            return
        }

        lastSocialError.value = null
    }

    const hideLastSocialError = () => {
        lastSocialError.value = null
    }

    return {
        availableSocials,
        connectedSocials,
        lastSocialError,

        loadConnectedSocials,

        initSocialLogin,
        confirmSocialLogin,

        connectSocial,
        confirmSocial,
        disconnectSocial,

        hideLastSocialError,
    }
})